import {
  type FieldValues,
  useController,
  type UseControllerProps,
  useFormContext,
} from "react-hook-form";
import { type ValidationOptions } from "@/components/common/formElements/types";
import { applyCheckboxGroupValidationOptions } from "@/components/common/formElements/utils/applyCheckboxGroupValidationOptions";
import { useTranslation } from "@/i18n/client";

export type CheckboxGroupFieldValidationOptions<
  TFieldValues extends FieldValues,
> = ValidationOptions<TFieldValues>;

export interface UseCheckboxGroupFieldControllerProps<
  TFieldValues extends FieldValues,
> extends Omit<UseControllerProps<TFieldValues>, "control" | "rules"> {
  rules?: CheckboxGroupFieldValidationOptions<TFieldValues>;
}

export function useCheckboxGroupFieldController<
  TFieldValues extends FieldValues,
>(props: UseCheckboxGroupFieldControllerProps<TFieldValues>) {
  const { rules = {}, name, ...restControllerProps } = props;

  const { t } = useTranslation("form-elements");
  const { control, getValues } = useFormContext<TFieldValues>();

  const checkboxGroupFieldRules =
    applyCheckboxGroupValidationOptions<TFieldValues>(
      name,
      getValues,
      t,
      rules,
    );
  const required = !!checkboxGroupFieldRules.required;

  const controllerProps = useController<TFieldValues>({
    name,
    control,
    rules: checkboxGroupFieldRules,
    ...restControllerProps,
  });

  return {
    required,
    ...controllerProps,
  };
}
